import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, type Centavos, type Settings } from '@abastece/shared';
import { lugarEnElLog, type Empleado, type PagoAguinaldo, type Planilla } from '@abastece/core';
import { db } from '../db/dexie.js';
import { descargar } from '../lib/archivos.js';
import {
  colillaDeAguinaldo,
  colillaDePlanilla,
  colillasEnPdf,
  juntarColillas,
  nombreParaArchivo,
  textoDeColilla,
  tituloDelPdf,
  type Colilla as ModeloColilla,
  type FormaDePagoPlanilla,
} from '../lib/colilla.js';
import { imprimirTexto } from '../lib/imprimir.js';
import { tituloDe } from '../lib/planilla.js';
import { datosNegocio, enlaceWhatsApp } from '../lib/recibo.js';
import { leerSinEfecto, tuvoEfecto } from '../lib/sinEfecto.js';
import { useT } from '../i18n/index.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/** One person's stub, as the sheet offers it. */
interface Colilla {
  empleadoId: string;
  nombre: string;
  /** What it says (`lib/colilla.ts`), for the PDF. */
  modelo: ModeloColilla;
  /** What they are handed, as their row says. */
  recibe: Centavos;
  /** On paper, with the line to sign. */
  impresa: string;
  /** Over WhatsApp, without it. */
  enviada: string;
  telefono: string | null;
}

/**
 * «Colillas» (the owner, 2026-11-01: «we need a function to print paystubs for each employee from the
 * payroll page»): a stub for each person on a run, a settlement or an aguinaldo payment, opened from its
 * card in «Corridas». Each person's row says what they receive and opens their stub as it will print,
 * with «WhatsApp», to their own number when their file has one, «Imprimir», and «Descargar PDF», a page
 * of US Letter (the owner, the same day: «can we also have a function that just makes a pdf for the pay
 * stub for download»). At the foot, «Imprimir todas» sends every stub to the roll at once, a line to cut
 * along between them, and «Descargar todas en PDF» makes one file of them, a page each. Read fresh from
 * the books while it is open: a correction synced meanwhile shows. With one person, theirs is open and
 * nothing else is.
 */
export function Colillas({
  corrida,
  aguinaldo,
  empleados,
  config,
  onCerrar,
}: {
  corrida?: Planilla | undefined;
  aguinaldo?: PagoAguinaldo | undefined;
  /** Everybody, those who left included: their papers and their phone. */
  empleados: readonly Empleado[];
  config: Settings;
  onCerrar: () => void;
}) {
  const t = useT();
  // How a run or a settlement was paid is on its payment's event (`planilla.pagada`), not on the run.
  const forma = useLiveQuery(
    async () => {
      if (!corrida?.pagada) return null;
      const [eventos, sinEfecto] = await Promise.all([db.eventos.where('type').equals('planilla.pagada').toArray(), leerSinEfecto()]);
      const suyo = eventos
        .filter((e) => tuvoEfecto(e, sinEfecto) && (e.payload as { planillaId?: string }).planillaId === corrida.id)
        .sort((a, b) => lugarEnElLog(a) - lugarEnElLog(b))
        .at(-1);
      return ((suyo?.payload as { formaPago?: FormaDePagoPlanilla } | undefined)?.formaPago ?? null) as FormaDePagoPlanilla | null;
    },
    [corrida?.id, corrida?.pagada],
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const negocio = datosNegocio(config);
  const porId = new Map(empleados.map((e) => [e.id, e]));
  const papeles = (id: string) => {
    const e = porId.get(id);
    return { cedula: e?.cedula ?? null, inssNumero: e?.inssNumero ?? null };
  };
  /** A stub's three forms: on paper, over WhatsApp, and what both say, for the PDF. */
  const como = (empleadoId: string, nombre: string, recibe: Centavos, modelo: ModeloColilla): Colilla => ({
    empleadoId,
    nombre,
    modelo,
    recibe,
    impresa: textoDeColilla(modelo, negocio, true),
    enviada: textoDeColilla(modelo, negocio, false),
    telefono: porId.get(empleadoId)?.telefono ?? null,
  });

  let colillas: Colilla[] = [];
  let titulo = '';
  if (corrida) {
    titulo = corrida.esLiquidacion ? t('planilla.liquidacionDe', { nombre: corrida.lineas[0]?.nombre ?? '' }) : tituloDe(corrida);
    colillas = corrida.lineas.map((l) =>
      como(
        l.empleadoId,
        l.nombre,
        (l.neto - (l.adelantoDescontado ?? 0)) as Centavos,
        colillaDePlanilla(corrida, l, {
          persona: papeles(l.empleadoId),
          pago: corrida.pagada && corrida.pagadaEn ? { fecha: corrida.pagadaEn, ...(forma ? { formaPago: forma } : {}) } : null,
          motivo: corrida.esLiquidacion ? (porId.get(l.empleadoId)?.motivoSalida ?? null) : null,
        }),
      ),
    );
  } else if (aguinaldo) {
    titulo = `${t('planilla.aguinaldo.titulo')} · ${aguinaldo.fecha}`;
    colillas = aguinaldo.lineas.map((l) =>
      como(l.empleadoId, l.nombre, l.monto, colillaDeAguinaldo(aguinaldo, l, { cargo: porId.get(l.empleadoId)?.cargo ?? null, persona: papeles(l.empleadoId) })),
    );
  }

  const [abierta, setAbierta] = useState<string | null>(colillas.length === 1 ? (colillas[0]?.empleadoId ?? null) : null);
  const varias = colillas.length > 1;

  /** The stubs as a PDF, a page each, downloaded: one person's, or everybody's in one file. */
  function pdf(cuales: readonly Colilla[]) {
    const primera = cuales[0];
    if (!primera) return;
    setError(null);
    try {
      const modelos = cuales.map((c) => c.modelo);
      const archivo =
        cuales.length === 1
          ? t('planilla.colillas.archivo', { nombre: nombreParaArchivo(primera.nombre), fecha: primera.modelo.dia })
          : t('planilla.colillas.archivoTodas', { fecha: primera.modelo.dia });
      descargar(new File([colillasEnPdf(modelos, negocio).bytes(tituloDelPdf(modelos))], archivo, { type: 'application/pdf' }));
    } catch {
      setError(t('planilla.colillas.error'));
    }
  }

  return (
    <Modal
      titulo={t('planilla.colillas.titulo')}
      onCerrar={onCerrar}
      pie={
        varias && (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="btn-primario w-full"
              data-imprimir-colillas
              onClick={() => imprimirTexto(juntarColillas(colillas.map((c) => c.impresa)))}
            >
              <Ico icono="imprimir" />
              {t('planilla.colillas.imprimirTodas', { n: colillas.length })}
            </button>
            {/* A size smaller under 360 px: at 320 its words broke over two lines. */}
            <button type="button" className="btn-secundario w-full max-[359px]:text-sm" data-pdf-colillas onClick={() => pdf(colillas)}>
              <Ico icono="descargar" />
              {t('planilla.colillas.pdfTodas')}
            </button>
          </div>
        )
      }
    >
      <div className="flex flex-col gap-4" data-colillas-de={corrida?.id ?? aguinaldo?.id}>
        <div>
          <p className="font-medium">{titulo}</p>
          <p className="mt-1 text-sm text-slate-400">{t('planilla.colillas.explica')}</p>
        </div>
        {error && <p className="text-sm text-rose-400">{error}</p>}
        <ul className="flex flex-col gap-2">
          {colillas.map((c) => {
            const esta = abierta === c.empleadoId;
            return (
              <li key={c.empleadoId} className="rounded-xl bg-slate-950/40 ring-1 ring-white/5 ring-inset" data-colilla-de={c.nombre}>
                {/* The row opens the stub, and closes it again. */}
                <button
                  type="button"
                  data-sin-icono="fila"
                  aria-expanded={esta}
                  className="flex min-h-12 w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-slate-800"
                  onClick={() => setAbierta(esta ? null : c.empleadoId)}
                >
                  <span className="min-w-0 truncate font-medium">{c.nombre}</span>
                  <span className="flex shrink-0 items-center gap-2 text-sm text-slate-300">
                    <span className="tabular" data-recibe>
                      {t('planilla.colillas.recibe', { monto: formatMoney(c.recibe) })}
                    </span>
                    <Ico icono="desplegar" className={`size-4 text-slate-500 transition ${esta ? 'rotate-180' : ''}`} />
                  </span>
                </button>
                {esta && (
                  <div className="px-3 pb-3">
                    {/* As it will print, line for line: the roll's thirty-two characters scroll sideways on a narrow phone. */}
                    <pre className="tabular mb-3 overflow-x-auto rounded-lg bg-slate-950 p-3 text-xs leading-relaxed text-slate-300" data-colilla>
                      {c.impresa}
                    </pre>
                    {/* «WhatsApp» and «Imprimir» side by side; the PDF under them, the whole row. */}
                    <div className="grid grid-cols-2 gap-2">
                      <a
                        className="btn-secundario h-11 min-h-0 min-w-0 gap-1.5 px-2 text-sm"
                        href={enlaceWhatsApp(c.enviada, c.telefono ?? undefined)}
                        target="_blank"
                        rel="noreferrer"
                        data-whatsapp-colilla
                      >
                        <Ico icono="whatsapp" className="size-4 shrink-0" />
                        WhatsApp
                      </a>
                      <button
                        type="button"
                        className="btn-secundario h-11 min-h-0 min-w-0 gap-1.5 px-2 text-sm"
                        data-imprimir-colilla
                        onClick={() => imprimirTexto(c.impresa)}
                      >
                        <Ico icono="imprimir" className="size-4 shrink-0" />
                        {t('comun.imprimir')}
                      </button>
                      <button
                        type="button"
                        className="btn-secundario col-span-2 h-11 min-h-0 min-w-0 gap-1.5 px-2 text-sm"
                        data-pdf-colilla
                        onClick={() => pdf([c])}
                      >
                        <Ico icono="descargar" className="size-4 shrink-0" />
                        {t('planilla.colillas.pdf')}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </Modal>
  );
}
